import { useApiPost, useApiQuery } from '@/api/main/query'
import { ApiError } from '@/api/types'
import { Currencies } from '@/entities/curency'
import { BaseCheckbox, BaseDialog, BaseInput, BaseMultiSelect } from '@/shared/components'
import { useToast } from '@/shared/hooks'
import { useEffect, useState } from 'react'
import { withdrawsAPITrader } from '../../api'
import { TraderBalanceAndChannelsModel } from '../../model'
import { useQueryClient } from '@tanstack/react-query'
import { useWithdrawsTrader } from '@/features/withdraws'
import { AllBanksTypes, AllMethodsTypes, bankNameToTypeMap, bankOptions, methodOptions } from 'syncra-storybook/shared/utils'

type Props = {
  modalEdit: boolean
  setModalEdit: (value: boolean) => void
  balance: number
}

export const EditPayoutDialog = ({
  modalEdit: modalEditPayout,
  setModalEdit: setModalEditPayout,
  balance,
}: Props) => {
  const { addToast } = useToast()
  const [amount, setAmount] = useState(balance.toString())
  const [methods, setMethods] = useState<any[]>([])
  const [minAmount, setMinAmount] = useState<string | null>(null);
  const [maxAmount, setMaxAmount] = useState<string | null>(null);
  const [selectedBanks, setSelectedBanks] = useState<(AllBanksTypes | '*')[]>([])
  const queryClient = useQueryClient()

  const { data: banksData } = useApiQuery<{ banks: AllBanksTypes[]; methods: AllMethodsTypes[] }>(
    ['banksMethods'],
    withdrawsAPITrader.banks,
    {
      country: 'ru',
      currency: Currencies.RUB,
    }
  )

  const channelData = useWithdrawsTrader({}).data.balanceChannelsData as TraderBalanceAndChannelsModel;

  useEffect(() => {
    const pickSmallestOrNull = (arr: Array<number | null | undefined>): string | null => {
      if (!arr || arr.length === 0) return null;
      if (arr.some((v) => v == null)) return null; // если есть null/undefined — возвращаем null
      const nums = arr.filter((v): v is number => typeof v === 'number');
      return nums.length ? String(Math.min(...nums)) : null;
    };

    if (channelData) {
      setAmount(channelData.availableAmount.toString());
      const minFromChannels = pickSmallestOrNull(channelData.channels?.map(c => c.minPaymentAmount) ?? []);
      const maxFromChannels = pickSmallestOrNull(channelData.channels?.map(c => c.maxPaymentAmount) ?? []);

      setMinAmount(minFromChannels);
      setMaxAmount(maxFromChannels);

      setSelectedBanks(channelData.banks.map(i => i === '' ? '*' : i) as AllBanksTypes[]);
    }
  }, [channelData, setAmount, setSelectedBanks]);

  useEffect(() => {
    if (!banksData?.methods) return; // ждём справочник
    const selected = new Set(channelData?.methods ?? []); // выбранные методы из API
    const mapped = banksData.methods.map((method, idx) => ({
      id: idx + 1,
      name: methodOptions[method]?.title||'',
      value: method,
      checked: selected.has(method),
      logo: methodOptions[method]?.icon || null,
    }));
    setMethods(mapped);
  }, [banksData?.methods, channelData?.methods]);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (/^\d*$/.test(value)) {
      setAmount(value)
    }
  }

  const handleMinAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setMinAmount(value === '' ? null : value);
    }
  };

  const handleMaxAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setMaxAmount(value === '' ? null : value);
    }
  };

  const handleCheckboxChange = (id: number) => {
    setMethods((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, checked: !item.checked } : item))
    )
  }

  const { mutate: updateBalanceChannels, isPending: isUpdating } = useApiPost<
    TraderBalanceAndChannelsModel,
    Omit<TraderBalanceAndChannelsModel, 'id' | 'channels'>
  >(withdrawsAPITrader.updateChannels, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['traderBalanceChannels'] })
      queryClient.invalidateQueries({ queryKey: ['withdraws'] })
    },
  })


  const handleSuccess = (message: string) => {
    addToast('Успех!', message, 'success')
  }

  const handleError = (error: ApiError) => {
    addToast('Ошибка!', error.message, 'error')
  }

  const handleSave = () => {
    const selectedMethods = methods.filter((method) => method.checked).map((m) => m.value)

    if (selectedMethods.length === 0) {
      addToast('Ошибка!', 'Необходимо выбрать хотя бы один метод', 'error')
      return
    }

    const isAllBanksSelected = selectedBanks.includes('*')
    const noBanksSelected = selectedBanks.length === 0
    const adjustedBanks = isAllBanksSelected || noBanksSelected ? [''] : selectedBanks

    const toNumOrNull = (v: string | null) => (v == null ? null : Number(v));

    const payload: Omit<TraderBalanceAndChannelsModel, 'id' | 'channels'> = {
      country: 'ru',
      currency: Currencies.RUB,
      availableAmount: Number(amount),
      minPaymentAmount: toNumOrNull(minAmount),
      maxPaymentAmount: toNumOrNull(maxAmount),
      banks: adjustedBanks,
      methods: selectedMethods,
    };

    updateBalanceChannels(payload, {
      onSuccess: () => {
        setModalEditPayout(false)
        handleSuccess('Данные обновлены')
      },
      onError: handleError,
    })
  }

  const allBanksOption = {
    label: 'Все банки',
    value: '*',
    logo: null,
  }

  const banksList = [
    allBanksOption,
    ...(banksData?.banks?.map((bank) => {
      // Маппим английское название на enum
      const bankEnum = bankNameToTypeMap[bank]
      const bankOption = bankEnum ? bankOptions[bankEnum] : null
      
      return {
        label: bankOption?.title ?? bank,
        value: bank,
        logo: bankOption?.icon ?? null,
      }
    }) || []),
  ]

  const selectedMethods = methods.filter((m) => m.checked).map((m) => m.value)
  const isSaveDisabled = isUpdating || !banksData?.methods || selectedMethods.length === 0

  return (
    <BaseDialog
      isVisible={modalEditPayout}
      onClose={() => setModalEditPayout(false)}
      onAction={handleSave}
      actionButtonName="Сохранить"
      title="Настройки"
      isLoading={isSaveDisabled}
    >
      <div className="flex flex-col gap-6 p-0.5 max-h-[600px] overflow-y-auto scrollbar-hide">
        <BaseInput
          label="Введите ваш баланс"
          placeholder="Сумма"
          variant="secondary"
          rightText="RUB"
          value={amount}
          onChange={handleAmountChange}
        />

        <div className="flex flex-col gap-2">
          <label className="text-text-secondary dark:text-white">Введите сумму заявки</label>
          <div className="flex gap-4">
            <BaseInput
              placeholder="От"
              value={minAmount ?? ''}
              onChange={handleMinAmountChange}
              className="flex-1"
            />
            <BaseInput
              placeholder="До"
              value={maxAmount ?? ''}
              onChange={handleMaxAmountChange}
              className="flex-1"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <p>Выберите метод оплаты</p>
          <ul className="flex flex-col gap-4 pr-4 max-h-[20vh] overflow-y-auto custom-scrollbar">
            {methods.map((item) => (
              <li key={item.id}>
                <BaseCheckbox
                  checked={item.checked}
                  onChange={() => handleCheckboxChange(item.id)}
                  fieldClassName="flex-row-reverse justify-between"
                  leftIcon={item.logo}
                  label={item.name}
                />
              </li>
            ))}
          </ul>
        </div>

        <BaseMultiSelect
          label="Выберите банки"
          options={banksList}
          value={selectedBanks}
          onChange={(vals) => {
            if ((vals as string[]).includes('*')) {
              setSelectedBanks(['*'])
            } else {
              setSelectedBanks(vals as AllBanksTypes[])
            }
          }}
        />
      </div>
    </BaseDialog>
  )
}
